import { useEffect, useRef, useState } from 'react';

// Detecta se é um dispositivo móvel para otimizar performance
const isMobile = () => {
  return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Configuração de layers para animações sincronizadas
const ANIMATION_LAYERS = {
  TITLE: { delay: 0, duration: 800, easing: 'ease-out' },
  CONTENT: { delay: 150, duration: 800, easing: 'ease-out' },
  BUTTON: { delay: 300, duration: 800, easing: 'ease-out' },
  IMAGE: { delay: 450, duration: 800, easing: 'ease-out' }
};

export const useScrollEffects = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const elementRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      
      // Calcula o progresso do scroll para efeitos de transform
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const progress = Math.min(currentScrollY / documentHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, []);

  return { scrollY, isVisible, elementRef, scrollProgress };
};

export const useParallax = (speed = 0.5) => {
  const { scrollY, elementRef } = useScrollEffects();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    // Reduz o efeito parallax em dispositivos móveis para melhor performance
    const effectiveSpeed = isMobile() ? speed * 0.3 : speed;
    
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      const elementTop = rect.top + window.scrollY;
      const parallaxOffset = (scrollY - elementTop) * effectiveSpeed;
      setOffset(parallaxOffset);
    }
  }, [scrollY, speed, elementRef]);

  return { offset, elementRef };
};

// Hook para animações baseadas em layers com timing sincronizado
export const useFadeInOnScroll = (layerType = 'CONTENT') => {
  const { isVisible, elementRef, scrollProgress } = useScrollEffects();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  // Usa a configuração de layer ou delay customizado
  const animationConfig = ANIMATION_LAYERS[layerType] || { delay: 0, duration: 800, easing: 'ease-out' };

  useEffect(() => {
    if (isVisible && !shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, animationConfig.delay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, animationConfig.delay, shouldAnimate]);

  // Calcula transform baseado no progresso do scroll para efeito de layer
  const getTransformStyle = () => {
    if (!isVisible) return {};
    
    const layerOffset = {
      'TITLE': 0,
      'CONTENT': 0.1,
      'BUTTON': 0.2,
      'IMAGE': 0.3
    };
    
    const offset = layerOffset[layerType] || 0;
    const translateY = Math.max(0, (1 - scrollProgress - offset) * 20);
    
    return {
      transform: `translateY(${translateY}px)`,
      transition: `all ${animationConfig.duration}ms ${animationConfig.easing}`
    };
  };

  return { 
    isVisible: shouldAnimate, 
    elementRef, 
    transformStyle: getTransformStyle(),
    animationConfig
  };
};

// Hook compatível com delay customizado (para manter compatibilidade)
export const useFadeInOnScrollWithDelay = (delay = 0) => {
  const { isVisible, elementRef } = useScrollEffects();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (isVisible && !shouldAnimate) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isVisible, delay, shouldAnimate]);

  return { isVisible: shouldAnimate, elementRef };
};